import { Platform } from '@angular/cdk/platform';
import { ComponentRef, inject, Injectable, ViewContainerRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, first } from 'rxjs';

import { NinetyMobileBannerComponent } from './mobile-banner.component';

@Injectable({
  providedIn: 'root',
})
export class NinetyMobileBannerService {
  private readonly _platform = inject(Platform);

  private _viewContainerRef: ViewContainerRef | undefined;
  private _mobileBannerComponent: ComponentRef<NinetyMobileBannerComponent> | undefined;

  private _router = inject(Router);

  private _LOCAL_STORAGE_KEY = 'ninetyMobileBanner';
  private _DAYS_DISMISSED = 30;

  constructor() {
    this._router.events
      .pipe(
        filter(ev => ev instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        const shouldShow = this._shouldShowBanner();
        if (shouldShow && !this._mobileBannerComponent && this._viewContainerRef) {
          this._showBanner();
        } else if (!shouldShow && this._mobileBannerComponent) {
          this._mobileBannerComponent.destroy();
          this._mobileBannerComponent = undefined;
        }
      });
  }

  initialize(viewContainerRef: ViewContainerRef): void {
    this._viewContainerRef = viewContainerRef;

    if (this._shouldShowBanner()) {
      this._showBanner();
    }
  }

  private _showBanner() {
    // Create the component on the apps view container
    this._mobileBannerComponent = this._viewContainerRef.createComponent(NinetyMobileBannerComponent);

    // Set the component to be destroyed when the banner is dismissed
    this._mobileBannerComponent.instance.dismiss.pipe(first()).subscribe(() => {
      this._setDismissalInStorage();

      this._mobileBannerComponent?.destroy();
      this._mobileBannerComponent = undefined;
    });
  }

  // Rules that determine if we should show the banner
  private _shouldShowBanner(): boolean {
    if (this._showOnPlatform() && !this._inDismissalPeriod() && this._showOnPages()) {
      return true;
    }
    return false;
  }

  // Determine what platforms we want to show the banner on
  // If on mobile and coming from our app we dont show
  private _showOnPlatform(): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    if ((this._platform.ANDROID || this._platform.IOS) && !urlParams.has('from', 'mobileapp')) {
      return true;
    }
    return false;
  }

  // Determine which routes we want to dont want to show it on
  private _showOnPages(): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    // Don't show if it's from mobile app
    // Don't show if it's the mobile sign-up complete page
    return (
      !urlParams.has('from', 'mobileapp') &&
      !window.location.pathname.includes('mobile-sign-up-complete') &&
      !window.location.pathname.includes('sign-up')
    );
  }

  private _inDismissalPeriod(): boolean {
    // Do they have a storage item set?
    const dismissalDateString = localStorage.getItem(this._LOCAL_STORAGE_KEY);
    if (dismissalDateString) {
      // Has x days passed since the date set?
      const now = new Date();
      const dismissalDate = new Date(dismissalDateString);
      if (this._daysBetween(now, dismissalDate) <= this._DAYS_DISMISSED) {
        return true;
      }
    }
    return false;
  }

  private _setDismissalInStorage(): void {
    localStorage.setItem(this._LOCAL_STORAGE_KEY, new Date().toISOString());
  }

  private _daysBetween(date1, date2) {
    const timeDiff = Math.abs(date2.getTime() - date1.getTime());
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
    return daysDiff;
  }
}
